import React, { useState } from 'react';
import { useProjectAccessAbility } from 'context/projectAccess/ProjectAccessContext';
import { useIsWellSaidAdmin } from 'context/user/UserContext';
import { LEGACY_STUDIO_RETIREMENT_HELP_HREF } from 'components/ProjectsV2/Create/components/LegacyStudioRetirementNotice';
import {
  Project,
  StudioProjectVersion,
} from '../../../../../server/graphql/__generated.types';
import {
  ProjectAccessAction,
  ProjectAccessSubject,
} from '../../../../../server/abilities/ProjectAccessEnums';
import { getLegacyBannerPhase } from '../legacyProjectRetirement';
import LegacyProjectBannerContent from './LegacyProjectBanner';
import LegacyProjectMigrationModal from './LegacyProjectMigrationModal';

/** Fields this banner reads; wider than full {@link Project} so Apollo `user.project` shapes assign cleanly. */
export type LegacyProjectBannerProject = Pick<
  Project,
  'id' | 'name' | 'studioVersion' | 'clipsCount'
>;

interface LegacyProjectBannerProps {
  project: LegacyProjectBannerProject;
}

export default function LegacyProjectBanner({
  project,
}: LegacyProjectBannerProps) {
  const [isLegacyConversionModalOpen, setIsLegacyConversionModalOpen] =
    useState(false);
  const isWellSaidAdmin = useIsWellSaidAdmin();
  const projectAccessAbility = useProjectAccessAbility();
  const canMigrateProject = projectAccessAbility.can(
    ProjectAccessAction.Migrate,
    ProjectAccessSubject.Project
  );
  const legacyProject =
    project.studioVersion !== StudioProjectVersion.Universal;

  const CLIP_COUNT_MIGRATION_LIMIT = 2000;
  let canMigrateToUniversalStudio = false;

  if (
    project.studioVersion === StudioProjectVersion.ScriptStudio &&
    canMigrateProject
  ) {
    canMigrateToUniversalStudio = true;
  } else {
    canMigrateToUniversalStudio = Boolean(
      legacyProject && project.clipsCount <= CLIP_COUNT_MIGRATION_LIMIT
    );
  }

  if (!canMigrateToUniversalStudio) {
    return null;
  }

  // todo: before launching make sure to not blanket return `preViewOnlyAnnouncement`
  const phase = getLegacyBannerPhase();

  return (
    <>
      <LegacyProjectBannerContent
        phase={phase}
        isWellSaidAdmin={isWellSaidAdmin}
        learnMoreHref={LEGACY_STUDIO_RETIREMENT_HELP_HREF}
        onGetStartedClick={() => {
          setIsLegacyConversionModalOpen(true);
        }}
      />
      <LegacyProjectMigrationModal
        open={isLegacyConversionModalOpen}
        projectId={project.id}
        projectName={project.name}
        studioVersion={project.studioVersion}
        helpArticleHref={LEGACY_STUDIO_RETIREMENT_HELP_HREF}
        onClose={() => {
          setIsLegacyConversionModalOpen(false);
        }}
      />
    </>
  );
}
